import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import {
  Fragment,
  useState,
  type CSSProperties,
  type PropsWithChildren,
} from 'react';
import styled from 'styled-components';

import { CardPackCategory } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXS } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { isType } from '@sorare/core/src/lib/gql';
import { getInGameCurrencyImageUrl } from '@sorare/core/src/lib/rewards.tsx';
import { thinMaterial } from '@sorare/core/src/style/utils';

import { VoucherPaymentDialog } from '#src/components/commonCard/BuyPacks/GemsPack/VoucherPaymentDialog/index.tsx';

import type { BundledPurchasableItem_bundledPurchasableItem } from './__generated__/index.graphql.ts';

const Root = styled(ButtonBase)`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  position: relative;
  border-radius: var(--triple-unit) var(--triple-unit) var(--double-unit)
    var(--double-unit);
  padding: var(--double-unit) var(--unit) var(--unit);
  justify-content: space-between;
  text-align: center;
  border: 1px solid var(--c-nd-150);
  cursor: pointer;
  ${thinMaterial}
  &:hover {
    background: var(--c-nd-50-opaque);
  }
`;

const Container = styled(Vertical).attrs({ center: true, gap: 2 })`
  flex-grow: 1;
  justify-content: flex-start;
`;

const ItemPicture = styled(Img)`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  object-fit: contain;
  &.isGold {
    width: calc(6 * var(--unit));
    height: calc(6 * var(--unit));
    filter: drop-shadow(0px 0px 16.8px rgba(251, 255, 26, 0.35));
  }
`;

const ItemDivider = styled.div`
  height: 1px;
  width: calc(6 * var(--unit));
  background-color: var(--c-nd-150);
`;

type Props = {
  bundledPurchasableItem: BundledPurchasableItem_bundledPurchasableItem;
  onPackPurchased: (offerId: string) => void;
  voucherId: string;
};

export const BundledPurchasableItem = ({
  bundledPurchasableItem,
  onPackPurchased,
  voucherId,
  children,
}: PropsWithChildren<Props>) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const [dialogOpened, setDialogOpened] = useState(false);

  const {
    id: purchasableItemId,
    price: bundledPrice,
    purchasableItems,
  } = bundledPurchasableItem;

  const price = toMonetaryAmount(bundledPrice);
  const { main } = useAmountWithConversion({ monetaryAmount: price });

  if (price.isZero || purchasableItems.length === 0) return null;
  return (
    <>
      <Root
        onClick={() => {
          setDialogOpened(true);
        }}
      >
        {children}
        <Container>
          {purchasableItems.map((item, index) => {
            let itemPictureUrl = '';
            let label = '';
            let isGold = false;
            if (isType(item, 'InGameCurrencyPurchasableItem')) {
              itemPictureUrl = getInGameCurrencyImageUrl(item.currency);
              label = `x${item.totalAmount}`;
            }
            if (isType(item, 'CardShardsPurchasableItem')) {
              itemPictureUrl = item.pictureUrl || '';
              label = `x${item.quantity}`;
            }
            if (isType(item, 'PackConfigPurchasableItem')) {
              isGold = item.cardPack.category === CardPackCategory.GOLD;
              itemPictureUrl = item.cardPack.pictureUrl;
              // Ugly but get only silver/gold from title
              label = `x1 ${isGold ? 'Gold' : ''}`;
            }
            return (
              <Fragment key={item.slug}>
                {index > 0 && <ItemDivider />}
                <Horizontal gap={0.5} center>
                  <ItemPicture
                    src={itemPictureUrl}
                    alt=""
                    className={classnames({ isGold })}
                  />
                  {isGold ? (
                    <BrandS
                      style={
                        {
                          '--text-gradient-color':
                            'linear-gradient(217.81deg, #FFFFFF 1.37%, #FCFF63 36.66%, #C26900 101.57%)',
                        } as CSSProperties
                      }
                      className="text-gradient-color whitespace-nowrap"
                    >
                      {label}
                    </BrandS>
                  ) : (
                    <BrandXS className="'whitespace-nowrap">{label}</BrandXS>
                  )}
                </Horizontal>
              </Fragment>
            );
          })}
        </Container>

        <Button asSpan size="small">
          {main}
        </Button>
      </Root>
      <VoucherPaymentDialog
        open={dialogOpened}
        onClose={() => setDialogOpened(false)}
        purchasableItemId={purchasableItemId}
        voucherId={voucherId}
        price={price}
        onPackPurchased={onPackPurchased}
      />
    </>
  );
};

BundledPurchasableItem.fragments = {
  bundledPurchasableItem: gql`
    fragment BundledPurchasableItem_bundledPurchasableItem on BundledPurchasableItem {
      slug
      id
      pictureUrl
      price {
        ...useMonetaryAmount_monetaryAmount
      }
      purchasableItems {
        slug
        ... on PackConfigPurchasableItem {
          slug
          pictureUrl
          cardPack {
            slug
            title
            pictureUrl
            category
          }
        }
        ... on InGameCurrencyPurchasableItem {
          slug
          pictureUrl
          totalAmount
          currency
        }
        ... on CardShardsPurchasableItem {
          slug
          pictureUrl
          quantity
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<BundledPurchasableItem_bundledPurchasableItem>,
};
